import { motion } from '@eversports/react-components/design-tokens/motion'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import PyroForm from 'pyro-form'
import React, { useEffect, useMemo } from 'react'

import { Localized } from '../../localization/react'
import {
  ConfirmWithOption,
  getCurrentTicketPrice,
  isPurchaseOptionAMembership,
  isPurchaseOptionAService,
  isPurchaseOptionATicket,
  ProductSelectionInitialValues,
  PurchaseOption,
} from '../../typings/Product'

import AssignedProductsList from './AssignedProductsList'
import PurchaseOptionsList from './PurchaseOptionsList'

interface Props {
  initialValues: ProductSelectionInitialValues
  purchaseOptions: Array<PurchaseOption>
  assignedProducts: Array<ConfirmWithOption>
  onProductSelection: (settleWithId: string) => void
}

const ProductSelectionComponent: React.FC<React.PropsWithChildren<Props>> = ({
  initialValues,
  onProductSelection,
  purchaseOptions,
  assignedProducts,
}) => {
  const handleChange = (values: ProductSelectionInitialValues): void => {
    onProductSelection(values.settleWithId)
  }

  const orderedPurchaseOptions = useMemo(() => {
    const sortedServices = purchaseOptions
      .filter(isPurchaseOptionAService)
      .sort((a, b) => a.price.value - b.price.value)
    const sortedMemberships = purchaseOptions.filter(isPurchaseOptionAMembership).sort((a, b) => {
      const aValue = a.paymentPlan.initial ? a.paymentPlan.initial.value : a.paymentPlan.recurring.value
      const bValue = b.paymentPlan.initial ? b.paymentPlan.initial.value : b.paymentPlan.recurring.value
      return aValue - bValue
    })
    const sortedTickets = purchaseOptions
      .filter(isPurchaseOptionATicket)
      .sort((a, b) => getCurrentTicketPrice(a).value - getCurrentTicketPrice(b).value)

    return [...sortedServices, ...sortedMemberships, ...sortedTickets]
  }, [purchaseOptions])

  const trialOfferOptions = orderedPurchaseOptions.filter(
    (purchaseOption) => purchaseOption.__typename === 'Card' && purchaseOption.isTrialCard,
  )
  const orderedPurchaseOptionsWithoutTrialOffer = orderedPurchaseOptions.filter(
    (purchaseOption) => !trialOfferOptions.includes(purchaseOption),
  )

  useEffect(() => {
    if (initialValues.settleWithId) return

    const hasOnlyOnePurchaseOption = purchaseOptions && purchaseOptions.length === 1 && assignedProducts.length === 0
    if (!hasOnlyOnePurchaseOption) return

    onProductSelection(purchaseOptions[0].id)
    initialValues.settleWithId = purchaseOptions[0].id
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ ease: 'easeInOut' }}
    >
      <Heading is="h3" marginBottom="wide">
        <Localized id="participation-options" />
      </Heading>
      <PyroForm initialValues={initialValues} onChange={handleChange}>
        <Flex flexDirection="column" verticalSpacing="wide">
          {assignedProducts.length > 0 && (
            <Flex flexDirection="column" data-testid="assigned-products-container">
              <Heading is="h4" variant="small">
                <Localized id="my-passes" />
              </Heading>
              <AssignedProductsList products={assignedProducts} />
            </Flex>
          )}
          {trialOfferOptions.length > 0 && (
            <Flex flexDirection="column" data-testid="purchasable-trial-offers-container">
              <Heading is="h4" variant="small">
                <Localized id="trial-offers" />
              </Heading>
              <PurchaseOptionsList products={trialOfferOptions} />
            </Flex>
          )}
          {orderedPurchaseOptionsWithoutTrialOffer.length > 0 && (
            <Flex flexDirection="column" data-testid="purchasable-products-container">
              <Heading is="h4" variant="small">
                {assignedProducts.length > 0 ? <Localized id="additional-pass" /> : <Localized id="new-pass" />}
              </Heading>
              <PurchaseOptionsList products={orderedPurchaseOptionsWithoutTrialOffer} />
            </Flex>
          )}
        </Flex>
      </PyroForm>
    </motion.div>
  )
}

export default ProductSelectionComponent
